import type { FC, HTMLAttributes } from 'react';
import { forwardRef } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import { Bullets, Lines, Numbers } from '../../pagination';
import type {
  PaginationProps as NewPaginationProps,
  PaginationVariantBaseProps
} from '../../pagination/types';
import { useSlider } from '../../SliderContext';

interface PaginationProps {
  settings: NewPaginationProps;
  position: string;
}

export const Pagination = forwardRef<HTMLDivElement, PaginationProps>(function (
  { settings, position },
  ref
) {
  const { appClassnamePrefix } = useSlider();

  const { type, customPagination } = settings;

  const PaginationType: FC<PaginationVariantBaseProps> = {
    bullets: Bullets,
    numbers: Numbers,
    lines: Lines
  }[type];

  return (
    <StyledPagination
      role={type === 'numbers' ? undefined : 'group'}
      aria-label={type === 'numbers' ? undefined : 'Choose slide to display'}
      _appClassPrefix={appClassnamePrefix}
      _position={position}
      ref={ref}
    >
      {customPagination ? (
        customPagination(settings)
      ) : (
        <PaginationType {...settings} />
      )}
    </StyledPagination>
  );
});

interface StyledPaginationProps extends HTMLAttributes<HTMLDivElement> {
  _appClassPrefix: string;
  _position: string;
}

const StyledPagination = styled.div.attrs<StyledPaginationProps>(
  ({ _appClassPrefix }) => ({
    className: classNames(`${_appClassPrefix}-pagination`)
  })
)`
  display: flex;
  bottom: 0;
  z-index: 1;
  max-width: 100%;
  justify-content: center;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }

  ${({ _position }) =>
    _position.startsWith('bottom')
      ? 'position: static; width: auto; left: unset; transform: translateX(0);'
      : 'position: absolute; width: 100%; left: 50%; transform: translateX(-50%);'}
`;
